'use client'
import { useHeaderTheme } from '@/providers/HeaderTheme'
import Image from 'next/image'
import Link from 'next/link'
import { usePathname } from 'next/navigation'
import React, { useEffect, useState } from 'react'

import type { Header, Identity } from '@/payload-types'

import { HeaderActions } from './HeaderActions'
import { MobileMenu } from './MobileMenu'
import { PillCap } from '@/components/ui/PillCap'
import './header.scss'

interface HeaderClientProps {
  data: Header
  identity: Identity
}

export const HeaderClient: React.FC<HeaderClientProps> = ({ data, identity }) => {
  /* Storing the value in a useState to avoid hydration errors */
  const [theme, setTheme] = useState<string | null>(null)
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [isScrolled, setIsScrolled] = useState(false)
  const [activeMenuIndex, setActiveMenuIndex] = useState<number | null>(null)
  const [isTransitioning, setIsTransitioning] = useState(false)
  const { headerTheme, setHeaderTheme } = useHeaderTheme()
  const pathname = usePathname()

  const handleMenuItemHover = (index: number | null) => {
    if (index === activeMenuIndex) return

    if (activeMenuIndex !== null && index !== null) {
      // Fermer d'abord, puis ouvrir le nouveau
      setIsTransitioning(true)
      setActiveMenuIndex(null)
      setTimeout(() => {
        setActiveMenuIndex(index)
        setIsTransitioning(false)
      }, 200)
    } else {
      setActiveMenuIndex(index)
    }
  }

  useEffect(() => {
    setHeaderTheme(null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname])

  useEffect(() => {
    if (headerTheme && headerTheme !== theme) setTheme(headerTheme)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [headerTheme])

  // Scroll detection for is-scrolled class (desktop only)
  useEffect(() => {
    const handleScroll = () => {
      // Only apply is-scrolled on desktop (xl breakpoint = 1200px)
      const isDesktop = window.innerWidth >= 1200

      if (!isDesktop) {
        setIsScrolled(false)
        document.body.classList.remove('is-scrolled')
        return
      }

      const scrollPosition = window.pageYOffset || document.documentElement.scrollTop
      const scrolled = scrollPosition > 15

      setIsScrolled(scrolled)

      if (scrolled) {
        document.body.classList.add('is-scrolled')
      } else {
        document.body.classList.remove('is-scrolled')
      }
    }

    const handleResize = () => {
      handleScroll()
    }

    window.addEventListener('scroll', handleScroll)
    window.addEventListener('resize', handleResize)

    // Initial check
    handleScroll()

    return () => {
      window.removeEventListener('scroll', handleScroll)
      window.removeEventListener('resize', handleResize)
    }
  }, [])

  return (
    <>
      <header
        {...(theme ? { 'data-theme': theme } : {})}
        className={`${isScrolled ? 'is-scrolled' : ''} ${activeMenuIndex !== null && data.navItems?.[activeMenuIndex]?.megaMenuSlots ? 'sub-is-open' : ''}`}
      >
        <nav
          className={`navbar ${activeMenuIndex !== null && data.navItems?.[activeMenuIndex]?.color ? `submenu-color-${data.navItems[activeMenuIndex].color}` : ''}`}
        >
          <div className="top">
            <Link className="navbar-brand text-white m-0 p-0" href="/">
              {identity.logo && typeof identity.logo === 'object' && 'url' in identity.logo && (
                <Image
                  className="logo"
                  src={identity.logo.url || ''}
                  alt={identity.logo.alt || identity.siteName || 'Logo'}
                  width={30}
                  height={30}
                />
              )}
              {identity.siteName && (
                <span className="brandName">
                  {identity.siteName}
                  <span className="brandDot">.</span>
                </span>
              )}
            </Link>

            <HeaderActions data={data} onMenuItemHover={handleMenuItemHover} />

            <button
              id="menuToggler"
              className="d-block d-xl-none p-2 rounded-2"
              type="button"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              <div className="bg-success"></div>
              <div className="bg-success"></div>
              <div className="bg-success"></div>
            </button>
          </div>

          <div
            className={`underMenu ${
              activeMenuIndex !== null &&
              data.navItems?.[activeMenuIndex]?.megaMenuSlots &&
              !isTransitioning
                ? 'open'
                : ''
            }`}
            onMouseEnter={() => {
              if (activeMenuIndex !== null) handleMenuItemHover(activeMenuIndex)
            }}
            onMouseLeave={() => handleMenuItemHover(null)}
          >
            {activeMenuIndex !== null &&
              data.navItems?.[activeMenuIndex]?.megaMenuSlots &&
              (() => {
                const activeItem = data.navItems?.[activeMenuIndex]
                if (!activeItem) return null
                const itemColor = activeItem.color || 'white'
                return (
                  <div
                    className="withBorder"
                    style={{
                      borderTopColor: `var(--color-${itemColor})`,
                    }}
                  >
                    <div className="row g-4">
                      <div className="col-lg-8">
                        <div className="row g-5">
                          {activeItem.megaMenuSlots?.slice(0, 3).map((slot, idx) => (
                            <div key={idx} className="col-4">
                              <div className={`slot slot0${idx + 1}`}>
                                <h3 className="subMenuTitle">{slot.title}</h3>
                                <ul className="p-0">
                                  {slot.links?.map((linkItem, linkIdx) => (
                                    <li key={linkIdx}>
                                      <a href={linkItem.url}>
                                        <i className="icon icon-chevron-right-2"></i>
                                        {linkItem.label}
                                      </a>
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                      <div className="col-lg-4 h-100">
                        {activeItem.featuredPost && typeof activeItem.featuredPost === 'object' ? (
                          <div className="slot slot04">
                            <div className="pushedPost">
                              <a
                                href={`/posts/${activeItem.featuredPost.slug}`}
                                className="position-absolute w-100 d-block h-100"
                                style={{ zIndex: 1 }}
                              />
                              <div
                                className="bgi h160 mb-3 rounded-3 overflow-hidden"
                                style={{
                                  backgroundImage:
                                    activeItem.featuredPost.heroImage &&
                                    typeof activeItem.featuredPost.heroImage === 'object'
                                      ? `url(${activeItem.featuredPost.heroImage.url})`
                                      : 'url(/media/todd-kent-178j8tJrNlc-unsplash-600x400.jpg)',
                                }}
                              ></div>
                              <h3 className="subMenuTitle mb-3">{activeItem.featuredPost.title}</h3>
                              <span className="flexCSB">
                                Voir plus <i className="icon icon-arrow-right"></i>
                              </span>
                            </div>
                          </div>
                        ) : (
                          <div className="slot slot04">
                            <div
                              className="bgi h160 mb-3 rounded-3 overflow-hidden"
                              style={{
                                backgroundImage:
                                  'url(/media/todd-kent-178j8tJrNlc-unsplash-600x400.jpg)',
                              }}
                            ></div>
                            <h3 className="subMenuTitle mb-3">
                              Ceci est un article dans le megamenu bien visible
                            </h3>
                            <a href="#" className="btn btn-pill btn-pill-tint-success">
                              En savoir plus
                              <PillCap />
                            </a>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )
              })()}
          </div>
        </nav>
      </header>

      <MobileMenu
        data={data}
        identity={identity}
        isOpen={mobileMenuOpen}
        onClose={() => setMobileMenuOpen(false)}
      />
    </>
  )
}
